import { useFarmContext } from 'src/Elements/Farm'
import { WallboxHardwareModel } from '../models'
import useWallboxHardwareBulkQuery from './use-wallbox-hardware-bulk-query'
import useWallboxHardwareQuery from './use-wallbox-hardware-query'
import { useInstallationDevices, useInstallationSubDevices } from 'src/Libs/flow/installation/hooks'
import { useTypedSelector } from 'src/State/RootReducer'
import { isFlowTheme } from 'src/Libs/utils'
import { useMemo } from 'react'
import { isSubDeviceLinkedToWallboxHardware } from '../utils'

type UseWallboxHardwareByStorage = {
  data: WallboxHardwareModel[]
  isLoading: boolean
  isError: boolean
}

/**
 * Returns wallbox hardware data for the current storage, automatically choosing between bulk or single queries based on farm context
 */
const useWallboxHardwareByStorage = (): UseWallboxHardwareByStorage => {
  const installationId = useTypedSelector(state => state.storage.activeInstallation)
  const { isFarmMasterSelected } = useFarmContext()
  const { data: wallboxHardwareBulk, isLoading: isWallboxHardwareBulkLoading, isError: isWallboxHardwareBulkError } = useWallboxHardwareBulkQuery()
  const { data: wallboxHardware, isLoading: isWallboxHardwareLoading, isError: isWallboxHardwareError } = useWallboxHardwareQuery()

  const { data: devices } = useInstallationDevices(installationId, isFlowTheme)
  const emcId = devices?.emc?.id?.toString() ?? null
  const emcStorageId = devices?.emc?.deviceId?.toString() ?? null
  const { data: subDevices } = useInstallationSubDevices(installationId, emcId, isFlowTheme)

  const data = isFarmMasterSelected === true ? wallboxHardwareBulk : wallboxHardware
  const isLoading = isFarmMasterSelected === true ? isWallboxHardwareBulkLoading : isWallboxHardwareLoading
  const isError = isFarmMasterSelected === true ? isWallboxHardwareBulkError : isWallboxHardwareError

  const filteredData = useMemo(() => {
    return (data || []).filter(hardwareWallbox => {
      // ? AF-5388[detailed info] This filter is needed only for flow, since we want to display only wallboxes linked as subDevices.
      if (!isFlowTheme) return true
      const matchingSubDeviceLink = subDevices?.controlled?.find(subDevice =>
        isSubDeviceLinkedToWallboxHardware(subDevice, hardwareWallbox, emcStorageId, isFlowTheme)
      )

      return Boolean(matchingSubDeviceLink)
    })
  }, [data, emcStorageId, subDevices?.controlled])

  return {
    data: filteredData,
    isLoading,
    isError,
  }
}

export default useWallboxHardwareByStorage
